import React, { useEffect, useRef } from 'react';
import { Instagram, ArrowUpRight } from 'lucide-react';
import { Button } from './ui/button';
import { CONTACT } from '../data/mock';

/**
 * ============================================================
 *  INSTAGRAM SECTION — powered by Behold.so (free)
 * ============================================================
 *
 *  HOW TO ACTIVATE (one-time, ~5 mins):
 *  1) Go to https://behold.so/ and sign up free
 *  2) Connect your Instagram account: @ichiandoripetcare
 *  3) Create a feed widget. Behold will give you a Feed ID
 *     (looks like: "AbCdEf12_XyZ")
 *  4) Paste your Feed ID below — replace YOUR_BEHOLD_FEED_ID
 *  5) That's it. The feed will auto-update with new posts.
 *
 *  Free tier:
 *    - Up to 9 posts displayed
 *    - Refreshes every 24h
 *    - No watermark
 *
 *  Until activated, this section shows a beautiful "Follow Us
 *  on Instagram" CTA card pointing to your profile so the
 *  layout looks intentional and not empty.
 * ============================================================
 */
const BEHOLD_FEED_ID = 'YOUR_BEHOLD_FEED_ID'; // ← Replace this line only

const isConfigured = () =>
  BEHOLD_FEED_ID && !BEHOLD_FEED_ID.startsWith('YOUR_');

const InstagramSection = () => {
  const scriptLoaded = useRef(false);

  useEffect(() => {
    if (!isConfigured() || scriptLoaded.current) return;
    if (document.querySelector('script[data-behold]')) {
      scriptLoaded.current = true;
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://w.behold.so/widget.js';
    s.type = 'module';
    s.async = true;
    s.setAttribute('data-behold', 'true');
    document.body.appendChild(s);
    scriptLoaded.current = true;
  }, []);

  return (
    <section className="py-20 lg:py-24 bg-gradient-to-b from-white via-[#fdf3fc] to-white">
      <div className="max-w-[1280px] mx-auto px-5 lg:px-10">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 text-[#e870b6] font-semibold tracking-[0.2em] text-[13px] uppercase mb-3">
            <Instagram size={16} /> Follow Our Pack
          </span>
          <h2 className="font-serif text-[40px] lg:text-[52px] font-extrabold text-[#1a0b3d] mb-3 leading-tight">
            @ichiandoripetcare
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Real moments from the clinic, happy tails, grooming glow-ups and the pets who make every day worth it. Follow along and say hello.
          </p>
        </div>

        {isConfigured() ? (
          // eslint-disable-next-line react/no-unknown-property
          <behold-widget feed-id={BEHOLD_FEED_ID} />
        ) : null}

        <div className="text-center mt-2">
          <a href={CONTACT.instagram} target="_blank" rel="noreferrer">
            <Button className="rounded-full bg-gradient-to-r from-[#e870b6] via-[#d65aa1] to-[#c14b8e] hover:opacity-90 text-white px-8 py-6 h-auto gap-2 shadow-lg hover:shadow-2xl transition-all">
              <Instagram size={18} /> Follow on Instagram <ArrowUpRight size={16} />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

const FollowFallback = () => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto">
    {[1, 2, 3].map((i) => (
      <a
        key={i}
        href={CONTACT.instagram}
        target="_blank"
        rel="noreferrer"
        className="group relative aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-[#fdf3fc] via-[#f3d6f5] to-[#e870b6] shadow-md hover:shadow-2xl transition-all hover:-translate-y-1 flex items-center justify-center"
      >
        <div className="absolute inset-0 bg-gradient-to-tr from-[#5b3eb5]/10 via-transparent to-[#e870b6]/20 group-hover:from-[#5b3eb5]/20 transition-colors" />
        <div className="relative text-center px-6">
          <Instagram className="w-12 h-12 text-white mx-auto mb-3 drop-shadow-lg" />
          <p className="font-serif text-white font-extrabold text-[20px] drop-shadow-md">
            See our latest
          </p>
          <p className="text-white/90 text-[13px] mt-1">on Instagram</p>
        </div>
      </a>
    ))}
  </div>
);

export default InstagramSection;
